import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import prepareFilterParams from 'utils/prepareFilterParams'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Finance/Invoices/config/filters'
import { SET_INVOICES_FILTER } from 'modules/Finance/Invoices/store/types'
import { useInvoices } from 'modules/Finance/Invoices/hooks/useInvoices'
import { usePaymentProfiles } from 'modules/Finance/PaymentProfiles/hooks/usePaymentProfiles'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PageHeader from 'components/PageHeader'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import EmptyResults from 'components/EmptyResults'
import { DatedList, Icon } from 'gu-ui-kit'
import GridSpinner from 'components/UI/GridSpinner'
import Item from 'modules/Finance/Invoices/subcomponents/Item'
import styles from 'modules/Finance/Invoices/Invoices.module.scss'

import formatRelative from 'utils/dateFns/formatRelative'
import useLocationFilters from 'utils/hooks/useLocationFilters'

const Invoices = () => {
  const [page, setPage] = useState(1)
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()

  const {
    data = [],
    loading,
    loaded,
    failed,
    pagination = { limit: 50, offset: 0 },
    filters,
  } = useSelector((state) => state['finance/invoices'])

  const { paymentProfiles: { data: paymentProfilesData } = {} } = useSelector(
    (state) => state['finance/payment-profiles'],
  )
  const dispatch = useDispatch()
  const filtersPayload = useLocationFilters(global.location)

  const { getInvoices, dropInvoices, resetInvoices } = useInvoices()

  const { getPaymentProfiles, /*dropPaymentProfiles,*/ resetPaymentProfiles } = usePaymentProfiles()

  useEffect(() => {
    // dropPaymentProfiles()
    getPaymentProfiles({
      offset: 0,
      limit: 1000,
    })

    return () => {
      resetInvoices()
    }
  }, [])

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_INVOICES_FILTER,
    localStorageKey: 'finance/invoices',
  })

  useEffect(() => {
    if (!filterManager.filters) {
      //init filters from url if it exists
      dispatch({ type: SET_INVOICES_FILTER, payload: filtersPayload })

      return
    }
    dropInvoices()
    setPage(1)
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }

    const { limit } = pagination

    getInvoices({
      ...prepareFilterParams(filterManager.filters),
      offset: (page - 1) * limit,
      limit: limit,
    })
  }, [filterManager.filters, page, paymentProfilesData])

  useEffect(() => {
    if (!paymentProfilesData || filterManager.filters?.payment_profile_ids?.options?.length) {
      return
    }

    filterManager.updateFilterConfigurationByKey({
      key: 'payment_profile_ids',
      val: { options: paymentProfilesData.map(({ id, title }) => ({ value: id, label: title })) },
    })

    resetPaymentProfiles()
  }, [paymentProfilesData])

  const hasNotInvoices = useMemo(
    () =>
      filterManager.filters &&
      Object.keys(prepareFilterParams(filterManager.filters)).length === 0 &&
      data.length === 0,
    [data, filterManager.filters],
  )

  const items = useMemo(
    () =>
      data.map(({ created_at, ...rest }) => ({
        ...rest,
        date: created_at,
      })),
    [data],
  )

  if (!loaded) {
    return (
      <div className={'layout-tile'}>
        <GridSpinner />
      </div>
    )
  }

  if (loaded && !loading && hasNotInvoices) {
    return (
      <div className={'layout-tile'}>
        <EmptyResults
          header={t('invoices::totally_empty_results.heading')}
          text={t('invoices::totally_empty_results.summary')}
          icon={
            <Icon
              name={'document'}
              color={'Interface-Onsurface'}
            />
          }
        />
      </div>
    )
  }

  return (
    <>
      <div className={clsx('layout-tile', 'margin-bottom-16')}>
        <PageHeader>{t('invoices::heading')}</PageHeader>
      </div>
      <div className={'layout-tile'}>
        <FilterPanel
          filterManager={filterManager}
          className={breakpoint.md ? 'margin-bottom-32' : 'margin-bottom-16'}
        />
        <DatedList
          emptyComponent={
            <EmptyResults
              className={styles['transfers__empty-list']}
              icon={
                <Icon
                  name={'users'}
                  color={'Interface-Onsurface'}
                />
              }
              header={t('invoices::empty_results.heading')}
              text={t('invoices::empty_results.summary')}
            />
          }
          itemComponent={Item}
          pagination={pagination}
          className={styles['list']}
          items={items}
          moreButtonLabel={t('common::buttons.show_more')}
          handleGetMoreItems={() => setPage((page) => page + 1)}
          loading={loading}
          loaded={loaded}
          loadFailed={failed}
          l10n={{
            formatFunction: formatRelative,
            today: t('common::dated_list.date_format.today'),
            yesterday: t('common::dated_list.date_format.yesterday'),
            formatDates: {
              short: t('common::dated_list.date_format.short'),
              long: t('common::dated_list.date_format.long'),
            },
          }}
        />
      </div>
    </>
  )
}

export default Invoices
